import type {
  OutputConnections,
  Terminal,
} from '../models/CommissioningDevice';
import type {
  TConnectionGroup,
  ConnectionFormData,
  ConnectionFormDataPerPhase,
  ConnectionFormApiPayload,
} from '../models/Connection';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import {
  CONNECTION_TYPE,
  PHASE,
  TERMINAL_NAME_NO_CONNECTION,
  TERMINAL_NO_CONNECTION_DISPLAY_TEXT,
} from './constants';

/**
 * Optimistically get the device state to show Phase miswire message (if any).
 * But don't block the user from proceeding if the device is still not communicating.
 */
export async function optimisticallyGetCommissionState() {
  const commissioningDeviceStore = useCommissioningDeviceStore();
  try {
    await commissioningDeviceStore.fetchTerminalsState();
  } catch (error) {
    console.error('Error fetching commission state', error);
  }
}

/**
 * Generate the draft connections data to use for UI tenancy connection form based on the following data sources:
 *
 * @param rawDetectedConnections - Detected connections from device
 * @param backendData - Connection details that are retrieved from backend
 * @param draftConnections - Draft connections name that the user has entered (take precedence over backend data)
 *
 * @returns The form data with detected connections and previous draft taken into account
 */
export const generateDraftConnections = (
  rawDetectedConnections: OutputConnections,
  backendData: TConnectionGroup[],
  draftConnections: ConnectionFormData | null,
): ConnectionFormData => {
  const connectionsDataRef =
    draftConnections && draftConnections.length > 0
      ? draftConnections
      : transformToConnectionFormData(backendData);

  return Array.from({ length: 5 }, (_, index) => {
    const groupNumber = index + 1;
    const connGroupRef = connectionsDataRef[index].connectionGroup;
    const detectedConnections = {
      L1: rawDetectedConnections[`L1-${groupNumber}` as Terminal],
      L2: rawDetectedConnections[`L2-${groupNumber}` as Terminal],
      L3: rawDetectedConnections[`L3-${groupNumber}` as Terminal],
    };

    return {
      groupNumber: groupNumber,
      connectionGroup: {
        type: getConnectionTypeValue(detectedConnections, connGroupRef.type),
        L1: getPhaseDetails(detectedConnections.L1.ready, connGroupRef.L1),
        L2: getPhaseDetails(detectedConnections.L2.ready, connGroupRef.L2),
        L3: getPhaseDetails(detectedConnections.L3.ready, connGroupRef.L3),
      },
    };
  }) as ConnectionFormData;
};

const transformToConnectionFormData = (backendData: TConnectionGroup[]): ConnectionFormData => {
  return Array.from({ length: 5 }, (_, index) => ({
    groupNumber: index + 1,
    connectionGroup: {
      type: backendData[index]?.type ?? CONNECTION_TYPE.NO_SELECTED,
      L1: { name: backendData[index]?.L1 ?? undefined },
      L2: { name: backendData[index]?.L2 ?? undefined },
      L3: { name: backendData[index]?.L3 ?? undefined },
    },
  })) as ConnectionFormData;
};

const getConnectionTypeValue = (
  detectedConnectionGroup: Record<PHASE, { ready: boolean; state: string }>,
  currentType: CONNECTION_TYPE | undefined | null,
) => {
  const detectedContainsNoConnection =
    !detectedConnectionGroup.L1.ready ||
    !detectedConnectionGroup.L2.ready ||
    !detectedConnectionGroup.L3.ready;

  if (detectedContainsNoConnection) {
    // ignore current type as long as there's any phase not connected.
    return CONNECTION_TYPE.THREE_SINGLE;
  } else if (
    currentType === CONNECTION_TYPE.ONE_TRIPLE ||
    currentType === CONNECTION_TYPE.THREE_SINGLE
  ) {
    return currentType;
  } else {
    // Fallback to 3-single by default.
    return CONNECTION_TYPE.THREE_SINGLE;
  }
};

const getPhaseDetails = (detectedReady: boolean, phaseDetails: ConnectionFormDataPerPhase) => {
  return {
    name: getPhaseNameValue(detectedReady, phaseDetails.name),
    externalPropertyId: phaseDetails.externalPropertyId ?? undefined,
    externalPropertyIdSource: phaseDetails.externalPropertyIdSource ?? undefined,
  };
};

const getPhaseNameValue = (detectedReady: boolean, currentPhaseName: string | undefined) => {
  if (!detectedReady) {
    const hasPreviouslyEnteredName =
      currentPhaseName &&
      currentPhaseName !== '' &&
      currentPhaseName !== TERMINAL_NAME_NO_CONNECTION && // if the current name is populated by backend data
      currentPhaseName !== TERMINAL_NO_CONNECTION_DISPLAY_TEXT;

    return hasPreviouslyEnteredName ? currentPhaseName : TERMINAL_NO_CONNECTION_DISPLAY_TEXT;
  } else {
    const previouslyNoConnection =
      currentPhaseName === TERMINAL_NAME_NO_CONNECTION ||
      currentPhaseName === TERMINAL_NO_CONNECTION_DISPLAY_TEXT;
    if (previouslyNoConnection) {
      return '';
    } else {
      return currentPhaseName ?? '';
    }
  }
};

/**
 * Transform connections values to match backend API expectation.
 *
 * For terminal that are not connected, set as TERMINAL_NAME_NO_CONNECTION:
 * - If the value is empty string (eg: user cleared the input)
 * - If the value is TERMINAL_NO_CONNECTION_DISPLAY_TEXT (eg: user did not enter any value)
 *
 * Otherwise, keep the name user has entered.
 */
export const transformConnectionsForSubmission = (
  connections: ConnectionFormData,
  outputConnections: OutputConnections,
): ConnectionFormApiPayload => {
  return connections.map(({ groupNumber, connectionGroup }) => {
    const transformedName = (phase: PHASE) => {
      const terminal = `${phase}-${groupNumber}` as Terminal;
      const isNoConnection = !outputConnections[terminal]?.ready;
      const name = connectionGroup[phase]?.name?.trim() ?? '';
      return isNoConnection && (name === '' || name === TERMINAL_NO_CONNECTION_DISPLAY_TEXT)
        ? TERMINAL_NAME_NO_CONNECTION
        : name;
    };

    const l1 = connectionGroup.L1;
    const l2 = connectionGroup.L2;
    const l3 = connectionGroup.L3;

    return {
      type: connectionGroup.type,
      L1: {
        name: transformedName(PHASE.L1),
        ...__omitIfUndefined('externalPropertyId', l1.externalPropertyId),
        ...__omitIfUndefined('externalPropertyIdSource', l1.externalPropertyIdSource),
      },
      L2: {
        name: transformedName(PHASE.L2),
        ...__omitIfUndefined('externalPropertyId', l2.externalPropertyId),
        ...__omitIfUndefined('externalPropertyIdSource', l2.externalPropertyIdSource),
      },
      L3: {
        name: transformedName(PHASE.L3),
        ...__omitIfUndefined('externalPropertyId', l3.externalPropertyId),
        ...__omitIfUndefined('externalPropertyIdSource', l3.externalPropertyIdSource),
      },
    };
  });
};

function __omitIfUndefined(fieldName: string, value: unknown) {
  return value !== undefined ? { [fieldName]: value } : {};
}

/**
 * Collect all connection names from form data, excluding specified conditions
 * @param formData - The form data containing all connection groups
 * @param excludeIndex - Optional index to exclude (for current field validation)
 * @param excludePhase - Optional phase to exclude (for current field validation)
 * @returns Array of valid connection names to check for duplicates (normalized: trimmed and lowercased)
 */
export const collectAllConnectionNames = (
  formData: ConnectionFormData,
  excludeIndex?: number,
  excludePhase?: string,
): string[] => {
  const names: string[] = [];
  formData.forEach((item, index) => {
    (['L1', 'L2', 'L3'] as const).forEach((phase) => {
      if (index === excludeIndex && phase === excludePhase) return; // Skip current field
      if (isOneTripleL2L3(item.connectionGroup.type, phase)) return; // Only take L1 for one triple type

      const value = item.connectionGroup[phase].name;
      if (value === TERMINAL_NAME_NO_CONNECTION) return; // Skip "No Connection"
      const trimmedValue = value?.trim() ?? '';
      if (trimmedValue === '') return; // Skip empty strings
      names.push(trimmedValue.toLowerCase()); // Normalize for case-insensitive comparison
    });
  });
  return names;
};

const isOneTripleL2L3 = (type: CONNECTION_TYPE, phase: string) => {
  return type === CONNECTION_TYPE.ONE_TRIPLE && (phase === 'L2' || phase === 'L3');
};

/**
 * Create a uniqueness validator for Ant Design Vue form validation
 * @param formData - The form data containing all connection groups
 * @param currentIndex - Index of the current connection group
 * @param currentPhase - Phase being validated (L1, L2, or L3)
 * @returns Validator function for Ant Design Vue
 */
export const createUniquenessValidator = (
  formData: ConnectionFormData,
  currentIndex: number,
  currentPhase: PHASE,
) => {
  const uniquenessValidator = async (_rule: unknown, value: string) => {
    const trimmedValue = value?.trim();
    if (!trimmedValue || trimmedValue === TERMINAL_NO_CONNECTION_DISPLAY_TEXT)
      return Promise.resolve();
    const allNames = collectAllConnectionNames(formData, currentIndex, currentPhase);
    if (allNames.includes(trimmedValue.toLowerCase())) {
      return Promise.reject('Connection name must be unique');
    }
    return Promise.resolve();
  };

  return uniquenessValidator;
};

/**
 * Check if each phase has at least one live connection
 * @param detectedConnections - The detected connections from the device
 * @returns Object with boolean values for each phase
 */
export const checkLiveConnectionPerPhase = (detectedConnections: OutputConnections): Record<PHASE, boolean> => {
  const L1Terminals = Object.keys(detectedConnections).filter((key) => key.startsWith('L1-'));
  const L2Terminals = Object.keys(detectedConnections).filter((key) => key.startsWith('L2-'));
  const L3Terminals = Object.keys(detectedConnections).filter((key) => key.startsWith('L3-'));

  const hasLiveConnection = (terminals: string[]) => {
    return terminals.some((terminal) => detectedConnections[terminal as Terminal]?.ready);
  };

  return {
    L1: hasLiveConnection(L1Terminals),
    L2: hasLiveConnection(L2Terminals),
    L3: hasLiveConnection(L3Terminals),
  };
};